import { cva, type VariantProps } from 'class-variance-authority'
import { Tabs as TabsPrimitive } from 'radix-ui'
import * as React from 'react'

import { cn } from '@/lib/utils'

function Tabs({
  className,
  orientation = 'horizontal',
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      data-orientation={orientation}
      orientation={orientation}
      className={cn(
        'group/tabs flex gap-2 data-[orientation=horizontal]:flex-col',
        className,
      )}
      {...props}
    />
  )
}

const tabsListVariants = cva(
  `group/tabs-list inline-flex w-fit items-center justify-center
  group-data-[orientation=vertical]/tabs:h-fit
  group-data-[orientation=vertical]/tabs:flex-col data-[variant=line]:rounded-none`,
  {
    variants: {
      variant: {
        default: `flex gap-0.5 rounded-lg border bg-muted/50 p-0.5
          group-data-[orientation=horizontal]/tabs:h-auto`,
        line: `gap-1 bg-transparent
          group-data-[orientation=horizontal]/tabs:h-9`,
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

function TabsList({
  className,
  variant = 'default',
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants>) {
  return (
    <div className="overflow-x-auto overflow-y-hidden">
      <TabsPrimitive.List
        data-slot="tabs-list"
        data-variant={variant}
        className={cn(tabsListVariants({ variant }), className)}
        {...props}
      />
    </div>
  )
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        'cursor-pointer outline-none',
        // Default variant: segmented control (pill + slate active)
        `group-data-[variant=default]/tabs-list:text-foreground-dark
        group-data-[variant=default]/tabs-list:hover:bg-muted/80
        group-data-[variant=default]/tabs-list:focus-visible:ring-ring
        group-data-[variant=default]/tabs-list:inline-flex
        group-data-[variant=default]/tabs-list:h-8
        group-data-[variant=default]/tabs-list:min-h-8
        group-data-[variant=default]/tabs-list:shrink-0
        group-data-[variant=default]/tabs-list:items-center
        group-data-[variant=default]/tabs-list:justify-center
        group-data-[variant=default]/tabs-list:gap-1.5
        group-data-[variant=default]/tabs-list:rounded-md
        group-data-[variant=default]/tabs-list:border
        group-data-[variant=default]/tabs-list:border-transparent
        group-data-[variant=default]/tabs-list:px-3
        group-data-[variant=default]/tabs-list:text-xs
        group-data-[variant=default]/tabs-list:font-medium
        group-data-[variant=default]/tabs-list:whitespace-nowrap
        group-data-[variant=default]/tabs-list:transition-all
        group-data-[variant=default]/tabs-list:duration-150
        group-data-[variant=default]/tabs-list:focus-visible:ring-2
        group-data-[variant=default]/tabs-list:focus-visible:ring-offset-2
        group-data-[variant=default]/tabs-list:focus-visible:outline-none
        group-data-[variant=default]/tabs-list:disabled:pointer-events-none
        group-data-[variant=default]/tabs-list:disabled:opacity-50
        group-data-[variant=default]/tabs-list:data-[state=active]:bg-slate-700
        group-data-[variant=default]/tabs-list:data-[state=active]:text-white
        group-data-[variant=default]/tabs-list:data-[state=active]:shadow-none
        group-data-[variant=default]/tabs-list:data-[state=active]:hover:bg-slate-700
        group-data-[variant=default]/tabs-list:data-[state=active]:hover:text-white
        group-data-[variant=default]/tabs-list:[&_svg]:pointer-events-none
        group-data-[variant=default]/tabs-list:[&_svg]:size-4
        group-data-[variant=default]/tabs-list:[&_svg]:shrink-0`,
        // Line variant: underline + flexible width
        `group-data-[variant=line]/tabs-list:text-foreground/60
        group-data-[variant=line]/tabs-list:hover:text-foreground
        group-data-[variant=line]/tabs-list:focus-visible:border-ring
        group-data-[variant=line]/tabs-list:focus-visible:ring-ring/50
        group-data-[variant=line]/tabs-list:focus-visible:outline-ring
        group-data-[variant=line]/tabs-list:data-[state=active]:text-foreground
        group-data-[variant=line]/tabs-list:after:bg-foreground
        group-data-[variant=line]/tabs-list:relative
        group-data-[variant=line]/tabs-list:inline-flex
        group-data-[variant=line]/tabs-list:h-[calc(100%-1px)]
        group-data-[variant=line]/tabs-list:flex-1
        group-data-[variant=line]/tabs-list:items-center
        group-data-[variant=line]/tabs-list:justify-center
        group-data-[variant=line]/tabs-list:gap-1.5
        group-data-[variant=line]/tabs-list:rounded-md
        group-data-[variant=line]/tabs-list:border
        group-data-[variant=line]/tabs-list:border-transparent
        group-data-[variant=line]/tabs-list:bg-transparent
        group-data-[variant=line]/tabs-list:px-2
        group-data-[variant=line]/tabs-list:py-1
        group-data-[variant=line]/tabs-list:text-sm
        group-data-[variant=line]/tabs-list:font-medium
        group-data-[variant=line]/tabs-list:whitespace-nowrap
        group-data-[variant=line]/tabs-list:transition-all
        group-data-[variant=line]/tabs-list:group-data-[orientation=vertical]/tabs:w-full
        group-data-[variant=line]/tabs-list:group-data-[orientation=vertical]/tabs:justify-start
        group-data-[variant=line]/tabs-list:after:absolute
        group-data-[variant=line]/tabs-list:after:opacity-0
        group-data-[variant=line]/tabs-list:after:transition-opacity
        group-data-[variant=line]/tabs-list:group-data-[orientation=horizontal]/tabs:after:inset-x-0
        group-data-[variant=line]/tabs-list:group-data-[orientation=horizontal]/tabs:after:bottom-[-5px]
        group-data-[variant=line]/tabs-list:group-data-[orientation=horizontal]/tabs:after:h-0.5
        group-data-[variant=line]/tabs-list:group-data-[orientation=vertical]/tabs:after:inset-y-0
        group-data-[variant=line]/tabs-list:group-data-[orientation=vertical]/tabs:after:-right-1
        group-data-[variant=line]/tabs-list:group-data-[orientation=vertical]/tabs:after:w-0.5
        group-data-[variant=line]/tabs-list:focus-visible:ring-[3px]
        group-data-[variant=line]/tabs-list:focus-visible:outline-1
        group-data-[variant=line]/tabs-list:disabled:pointer-events-none
        group-data-[variant=line]/tabs-list:disabled:opacity-50
        group-data-[variant=line]/tabs-list:data-[state=active]:bg-transparent
        group-data-[variant=line]/tabs-list:data-[state=active]:shadow-none
        group-data-[variant=line]/tabs-list:data-[state=active]:after:opacity-100
        group-data-[variant=line]/tabs-list:[&_svg]:pointer-events-none
        group-data-[variant=line]/tabs-list:[&_svg]:shrink-0
        group-data-[variant=line]/tabs-list:[&_svg:not([class*=size-])]:size-4`,
        className,
      )}
      {...props}
    />
  )
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn('flex-1 outline-none', className)}
      {...props}
    />
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export { Tabs, TabsContent, TabsList, tabsListVariants, TabsTrigger }
